import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import type {
    IRamadanRiddleQuestionResponse,
    IRamadanRiddleRewardsResponse,
    IRamadanRiddleSubmitResponse,
    IRamadanRiddlesResponse,
} from "@types";
import type { RamadanRiddlesView } from "./riddles-views/types";
import { getCookie } from "@utils/Cookies";

type AsyncSlice<T> = {
    data: T | null;
    isLoading: boolean;
    error: string | null;
};

type RamadanRiddlesState = {
    currentView: RamadanRiddlesView;
    overview: AsyncSlice<IRamadanRiddlesResponse>;
    question: AsyncSlice<IRamadanRiddleQuestionResponse>;
    submitResult: AsyncSlice<IRamadanRiddleSubmitResponse>;
    rewards: AsyncSlice<IRamadanRiddleRewardsResponse>;
};

type RamadanRiddlesActions = {
    setView: (view: RamadanRiddlesView) => void;
    closeDrawer: () => void;
    setOverview: (data: IRamadanRiddlesResponse | null) => void;
    setOverviewLoading: (isLoading: boolean) => void;
    setOverviewError: (error: string | null) => void;
    setQuestion: (data: IRamadanRiddleQuestionResponse | null) => void;
    setQuestionLoading: (isLoading: boolean) => void;
    setQuestionError: (error: string | null) => void;
    setSubmitResult: (data: IRamadanRiddleSubmitResponse | null) => void;
    setSubmitResultLoading: (isLoading: boolean) => void;
    setSubmitResultError: (error: string | null) => void;
    setRewards: (data: IRamadanRiddleRewardsResponse | null) => void;
    setRewardsLoading: (isLoading: boolean) => void;
    setRewardsError: (error: string | null) => void;
    resetViewData: () => void;
};

type RamadanRiddlesContextValue = {
    state: RamadanRiddlesState;
    actions: RamadanRiddlesActions;
};

const RamadanRiddlesContext = createContext<RamadanRiddlesContextValue | undefined>(undefined);

const createSlice = <T,>(initialData: T | null = null): AsyncSlice<T> => ({
    data: initialData,
    isLoading: false,
    error: null,
});

const GUIDE_COOKIE_KEY = "ramadan_riddles_guide_seen";
const GUIDE_COOKIE_EXPIRY_DAYS = 90;

const stampOverviewBanner = (
    data: IRamadanRiddlesResponse | null
): IRamadanRiddlesResponse | null => {
    if (!data) return data;
    const banner = data.question_banner;
    if (!banner) return data;
    return {
        ...data,
        question_banner: { ...banner, client_timestamp: Date.now() },
    };
};

type RamadanRiddlesProviderProps = {
    children: React.ReactNode;
    onClose: () => void;
    initialOverview?: IRamadanRiddlesResponse | null;
};

export const RamadanRiddlesProvider: React.FC<RamadanRiddlesProviderProps> = ({
    children,
    onClose,
    initialOverview = null,
}) => {
    const [currentView, setCurrentView] = useState<RamadanRiddlesView>(() => {
        const Cookies = getCookie();
        const hasSeenGuide = Cookies.has(GUIDE_COOKIE_KEY);

        if (!hasSeenGuide) {
            Cookies.set(GUIDE_COOKIE_KEY, "1", {}, GUIDE_COOKIE_EXPIRY_DAYS);
            return "guide";
        }

        return "progress";
    });
    const [overview, setOverviewState] = useState<AsyncSlice<IRamadanRiddlesResponse>>(() =>
        createSlice(stampOverviewBanner(initialOverview))
    );
    const [question, setQuestionState] = useState<AsyncSlice<IRamadanRiddleQuestionResponse>>(() =>
        createSlice()
    );
    const [submitResult, setSubmitResultState] = useState<AsyncSlice<IRamadanRiddleSubmitResponse>>(
        () => createSlice()
    );
    const [rewards, setRewardsState] = useState<AsyncSlice<IRamadanRiddleRewardsResponse>>(() =>
        createSlice()
    );

    const resetViewData = useCallback(() => {
        setQuestionState(createSlice());
        setSubmitResultState(createSlice());
        setRewardsState(createSlice());
    }, []);

    const closeDrawer = useCallback(() => {
        onClose();
    }, [onClose]);

    const setOverview = useCallback((data: IRamadanRiddlesResponse | null) => {
        const stampedData = stampOverviewBanner(data);
        setOverviewState((prev) => ({ ...prev, data: stampedData }));
    }, []);

    const setOverviewLoading = useCallback((isLoading: boolean) => {
        setOverviewState((prev) => ({ ...prev, isLoading }));
    }, []);

    const setOverviewError = useCallback((error: string | null) => {
        setOverviewState((prev) => ({ ...prev, error, isLoading: false }));
    }, []);

    const setQuestion = useCallback((data: IRamadanRiddleQuestionResponse | null) => {
        setQuestionState((prev) => ({ ...prev, data }));
    }, []);

    const setQuestionLoading = useCallback((isLoading: boolean) => {
        setQuestionState((prev) => ({ ...prev, isLoading }));
    }, []);

    const setQuestionError = useCallback((error: string | null) => {
        setQuestionState((prev) => ({ ...prev, error, isLoading: false }));
    }, []);

    const setSubmitResult = useCallback((data: IRamadanRiddleSubmitResponse | null) => {
        setSubmitResultState((prev) => ({ ...prev, data }));
    }, []);

    const setSubmitResultLoading = useCallback((isLoading: boolean) => {
        setSubmitResultState((prev) => ({ ...prev, isLoading }));
    }, []);

    const setSubmitResultError = useCallback((error: string | null) => {
        setSubmitResultState((prev) => ({ ...prev, error, isLoading: false }));
    }, []);

    const setRewards = useCallback((data: IRamadanRiddleRewardsResponse | null) => {
        setRewardsState((prev) => ({ ...prev, data }));
    }, []);

    const setRewardsLoading = useCallback((isLoading: boolean) => {
        setRewardsState((prev) => ({ ...prev, isLoading }));
    }, []);

    const setRewardsError = useCallback((error: string | null) => {
        setRewardsState((prev) => ({ ...prev, error, isLoading: false }));
    }, []);

    const state = useMemo(
        () => ({
            currentView,
            overview,
            question,
            submitResult,
            rewards,
        }),
        [currentView, overview, question, submitResult, rewards]
    );

    const actions = useMemo(
        () => ({
            setView: setCurrentView,
            closeDrawer,
            setOverview,
            setOverviewLoading,
            setOverviewError,
            setQuestion,
            setQuestionLoading,
            setQuestionError,
            setSubmitResult,
            setSubmitResultLoading,
            setSubmitResultError,
            setRewards,
            setRewardsLoading,
            setRewardsError,
            resetViewData,
        }),
        [
            closeDrawer,
            resetViewData,
            setOverview,
            setOverviewLoading,
            setOverviewError,
            setQuestion,
            setQuestionLoading,
            setQuestionError,
            setSubmitResult,
            setSubmitResultLoading,
            setSubmitResultError,
            setRewards,
            setRewardsLoading,
            setRewardsError,
        ]
    );

    const value = useMemo(() => ({ state, actions }), [state, actions]);

    return (
        <RamadanRiddlesContext.Provider value={value}>{children}</RamadanRiddlesContext.Provider>
    );
};

const useRamadanRiddlesContext = () => {
    const context = useContext(RamadanRiddlesContext);

    if (!context) {
        throw new Error("useRamadanRiddlesContext must be used within RamadanRiddlesProvider");
    }

    return context;
};

export const useRiddlesNavigation = () => {
    const { state, actions } = useRamadanRiddlesContext();

    return {
        currentView: state.currentView,
        setView: actions.setView,
        closeDrawer: actions.closeDrawer,
    };
};

export const useRiddlesOverview = () => useRamadanRiddlesContext().state.overview;
export const useRiddlesQuestion = () => useRamadanRiddlesContext().state.question;
export const useRiddlesSubmitResult = () => useRamadanRiddlesContext().state.submitResult;
export const useRiddlesRewards = () => useRamadanRiddlesContext().state.rewards;

export const useRiddlesActions = () => useRamadanRiddlesContext().actions;
